import Text from '@shared/components/content/text';
import { cn } from '@shared/lib/utils';
import { ReactNode } from 'react';
import {
  CartesianGrid,
  Customized,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Cell,
  Tooltip,
  XAxis,
  XAxisProps,
  YAxis,
  YAxisProps,
  ZAxis,
} from 'recharts';
import { Margin } from 'recharts/types/util/types';
import { ChartContainer } from '../ui/chart';

export interface ScatterPoint {
  /** X value (e.g. spend, or unit price £/kg). */
  x: number;
  /** Y value (e.g. emissions, or carbon intensity). */
  y: number;
  /** Optional bubble magnitude — enables proportional sizing when present. */
  z?: number;
  /** Point name, shown as the tooltip title. */
  label: string;
  /** Stable identity for external highlighting (labels may collide). */
  id?: string;
  /** Metric rows listed in the tooltip beneath the name. */
  tooltipRows?: { label: string; value: string }[];
  /** Per-point fill; falls back to the chart's default colour. */
  color?: string;
}

interface ScatterChartProps {
  data: ScatterPoint[];
  className?: string;
  margin?: Margin;
  XAxisProps?: XAxisProps;
  YAxisProps?: YAxisProps;
  /** Bubble radius range [min, max] in px when points carry a `z`. */
  bubbleRange?: [number, number];
  /** Default point fill when a point has no `color`. */
  color?: string;
  /** Draws a horizontal reference line (e.g. an average) at this Y. */
  referenceY?: number;
  referenceLabel?: string;
  /** Formats axis ticks. */
  xFormatter?: (value: number) => string;
  yFormatter?: (value: number) => string;
  /** Optional legend row rendered above the plot. */
  legend?: ReactNode;
  /**
   * `id` of an externally-highlighted point (e.g. a hovered table row): it
   * gets a halo ring while the other points dim.
   */
  highlightedId?: string | null;
}

const DEFAULT_COLOR = 'var(--chart-food-and-drink)';

/** Card body shared by the hover tooltip and the pinned highlight card. */
function TooltipCard({ point }: { point: ScatterPoint }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-00 px-3 py-2 shadow-md">
      <Text className="text-sm font-medium text-primary-950">
        {point.label}
      </Text>
      {point.tooltipRows?.map((row) => (
        <div
          key={row.label}
          className="flex items-baseline justify-between gap-4"
        >
          <Text variant="subtle" className="text-xs">
            {row.label}
          </Text>
          <Text className="text-xs font-medium text-primary-900">
            {row.value}
          </Text>
        </div>
      ))}
    </div>
  );
}

/** Tooltip card: point name plus its metric rows (one card per hovered point). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ScatterTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  // Every axis (x/y/z) of the hovered point arrives as a payload entry —
  // they all share one datum, so render a single card from the first.
  const point = payload[0]?.payload as ScatterPoint | undefined;
  if (!point) return null;
  return <TooltipCard point={point} />;
}

/**
 * Pins the tooltip card next to the externally-highlighted point. Rendered via
 * recharts' Customized, which injects the chart's axis maps and plot offset;
 * the card sits on whichever side of the point has more room. The native
 * Tooltip can't do this — forcing it on for the highlight would leave a stale
 * card behind when the highlight clears.
 *
 * The injected xAxisMap/yAxisMap/offset props are recharts 2.x internals, not
 * public API — re-verify (or port to the v3 hooks) on a recharts major bump.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function HighlightCard(props: any) {
  const { xAxisMap, yAxisMap, offset, point } = props;
  const xScale = xAxisMap && Object.values(xAxisMap)[0];
  const yScale = yAxisMap && Object.values(yAxisMap)[0];
  if (!point || !xScale || !yScale || !offset) return null;

  const cx = xScale.scale(point.x);
  const cy = yScale.scale(point.y);
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;

  // Keep the card inside the plot: it opens away from whichever edges the
  // point is closest to (left/right, and top/bottom — the svg clips overflow).
  const flipX = cx > offset.left + offset.width / 2;
  const flipY = cy > offset.top + offset.height / 2;
  return (
    <foreignObject
      x={cx}
      y={cy}
      width={1}
      height={1}
      className="overflow-visible"
    >
      <div
        className="pointer-events-none w-max"
        style={{
          transform: `translate(${
            flipX ? 'calc(-100% - 14px)' : '14px'
          }, ${flipY ? 'calc(-100% + 14px)' : '-14px'})`,
        }}
      >
        <TooltipCard point={point} />
      </div>
    </foreignObject>
  );
}

/**
 * A scatter / bubble chart. Each point plots (x, y); an optional `z` drives
 * proportional bubble sizing. Points may be individually coloured (e.g. by
 * carbon rating) and a horizontal reference line drawn (e.g. an average).
 * Hover identifies points — a name plus metric rows per point.
 */
export default function SimpleScatterChart({
  data,
  className,
  margin = { top: 16, right: 24, bottom: 8, left: 0 },
  XAxisProps,
  YAxisProps,
  bubbleRange = [80, 640],
  color = DEFAULT_COLOR,
  referenceY,
  referenceLabel,
  xFormatter,
  yFormatter,
  legend,
  highlightedId,
}: ScatterChartProps) {
  const hasBubbles = data.some((point) => point.z != null);
  // The highlighted row may have no point on this chart (e.g. filtered out
  // for missing spend) — only then dim nothing.
  const highlightedPoint =
    highlightedId != null
      ? data.find((point) => (point.id ?? point.label) === highlightedId)
      : undefined;

  return (
    <div className="flex h-full w-full flex-col gap-3">
      {legend}
      {/* flex-1 min-h-0, not h-full: the plot takes the space remaining after
          the legend row instead of overflowing the fixed-height parent. */}
      <ChartContainer
        config={{}}
        className={cn('aspect-auto min-h-0 w-full flex-1', className)}
      >
        <ScatterChart margin={margin}>
          <CartesianGrid stroke="var(--border)" />
          <XAxis
            type="number"
            dataKey="x"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={xFormatter}
            {...XAxisProps}
          />
          <YAxis
            type="number"
            dataKey="y"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={52}
            tickFormatter={yFormatter}
            {...YAxisProps}
          />
          {hasBubbles && (
            <ZAxis type="number" dataKey="z" range={bubbleRange} />
          )}
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            content={<ScatterTooltip />}
          />
          {referenceY != null && (
            <ReferenceLine
              y={referenceY}
              stroke="var(--muted-foreground)"
              strokeDasharray="5 4"
              label={
                referenceLabel
                  ? {
                      value: referenceLabel,
                      position: 'insideTopRight',
                      fill: 'var(--muted-foreground)',
                      fontSize: 11,
                    }
                  : undefined
              }
            />
          )}
          <Scatter data={data} fill={color} isAnimationActive={false}>
            {data.map((point, index) => {
              const isHighlighted = point === highlightedPoint;
              const isDimmed = highlightedPoint != null && !isHighlighted;
              const fill = point.color ?? color;
              return (
                <Cell
                  key={index}
                  fill={fill}
                  fillOpacity={isHighlighted ? 1 : isDimmed ? 0.25 : 0.85}
                  stroke={isHighlighted ? fill : undefined}
                  strokeOpacity={isHighlighted ? 0.35 : undefined}
                  strokeWidth={isHighlighted ? 8 : undefined}
                />
              );
            })}
          </Scatter>
          {highlightedPoint && (
            <Customized
              key="highlight-card"
              component={<HighlightCard point={highlightedPoint} />}
            />
          )}
        </ScatterChart>
      </ChartContainer>
    </div>
  );
}
